import { CoreTypes, type Fill, type Stroke, type Style, type TextStyle } from '@vp/shared-types';
import { commands, DEFAULT_TEXT_STYLE, keyBetween, makeObject } from '@vp/core';
import { flattenBezier } from '@vp/render';
import { FONTS } from '../fonts';
import { live } from './bridge';

/**
 * SVG import: a drawing becomes objects of the canvas that can be edited — rectangles and ellipses,
 * lines, paths (curves and arcs, holes), text — with their fill, outline, opacity and transforms.
 * Styles come from presentation attributes, `<style>` rules, `style` attributes and parents;
 * gradients become their first color. Clip paths, masks, filters and patterns are not carried over.
 */

/** An object to create, in SVG user space (pixels after the root viewBox). */
export interface SvgShape {
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  props: Record<string, unknown>;
  style: Partial<Style>;
  /** `<image>` with embedded data: the picture to store as an attachment. */
  image?: Blob;
}

export interface SvgImport {
  shapes: SvgShape[];
  width: number;
  height: number;
  /** Elements left out (unsupported or over the limit). */
  skipped: number;
}

export const MAX_SVG_ELEMENTS = 5000;

type M = [number, number, number, number, number, number];
const I: M = [1, 0, 0, 1, 0, 0];
const mul = (m: M, n: M): M => [m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1], m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3], m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5]];
const apply = (m: M, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
const scaleOf = (m: M) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;

function num(v: string | null | undefined, d = 0): number {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) ? n : d;
}

/** `transform="translate(…) rotate(…) …"` as one matrix. */
export function parseTransform(s: string | null): M {
  let m = I;
  if (!s) return m;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  for (let r = re.exec(s); r; r = re.exec(s)) {
    const a = r[2].split(/[\s,]+/).filter(Boolean).map(Number);
    let n: M = I;
    switch (r[1]) {
      case 'matrix':
        if (a.length === 6) n = a as M;
        break;
      case 'translate':
        n = [1, 0, 0, 1, a[0] ?? 0, a[1] ?? 0];
        break;
      case 'scale':
        n = [a[0] ?? 1, 0, 0, a[1] ?? a[0] ?? 1, 0, 0];
        break;
      case 'rotate': {
        const t = ((a[0] ?? 0) * Math.PI) / 180;
        const c = Math.cos(t);
        const sn = Math.sin(t);
        const cx = a[1] ?? 0;
        const cy = a[2] ?? 0;
        n = mul(mul([1, 0, 0, 1, cx, cy], [c, sn, -sn, c, 0, 0]), [1, 0, 0, 1, -cx, -cy]);
        break;
      }
      case 'skewX':
        n = [1, 0, Math.tan(((a[0] ?? 0) * Math.PI) / 180), 1, 0, 0];
        break;
      case 'skewY':
        n = [1, Math.tan(((a[0] ?? 0) * Math.PI) / 180), 0, 1, 0, 0];
    }
    m = mul(m, n);
  }
  return m;
}

// ---------- paths ----------

/** A contour as editor nodes: [x, y, inX, inY, outX, outY]* (absolute), and whether it is closed. */
export interface Contour {
  nodes: number[];
  closed: boolean;
}

/** SVG path data → contours of cubic curves (lines, quadratic curves and arcs converted). */
export function parsePathData(d: string): Contour[] {
  const toks = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) ?? [];
  const out: Contour[] = [];
  let cur: Contour | null = null;
  let i = 0;
  let cmd = '';
  let x = 0;
  let y = 0;
  let sx = 0;
  let sy = 0;
  let lastC: [number, number] | null = null; // last cubic control (for S)
  let lastQ: [number, number] | null = null; // last quadratic control (for T)
  const isCmd = (t: string) => /^[a-zA-Z]$/.test(t);
  const n = () => Number(toks[i++]);
  const node = (px: number, py: number) => {
    cur!.nodes.push(px, py, px, py, px, py);
  };
  const cubic = (x1: number, y1: number, x2: number, y2: number, px: number, py: number) => {
    const L = cur!.nodes.length;
    cur!.nodes[L - 2] = x1;
    cur!.nodes[L - 1] = y1;
    cur!.nodes.push(px, py, x2, y2, px, py);
  };
  const close = () => {
    if (!cur) return;
    cur.closed = true;
    const N = cur.nodes;
    // the last point back on the first one: merge them
    if (N.length >= 12 && Math.abs(N[N.length - 6] - N[0]) < 1e-9 && Math.abs(N[N.length - 5] - N[1]) < 1e-9) {
      N[2] = N[N.length - 4];
      N[3] = N[N.length - 3];
      N.length -= 6;
    }
  };
  while (i < toks.length) {
    if (isCmd(toks[i])) cmd = toks[i++];
    else if (!cmd) {
      i++;
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    if (C === 'Z') {
      close();
      x = sx;
      y = sy;
      lastC = lastQ = null;
      cur = null;
      cmd = '';
      continue;
    }
    if (i >= toks.length || isCmd(toks[i])) {
      cmd = '';
      continue;
    }
    if (C === 'M') {
      x = (rel ? x : 0) + n();
      y = (rel ? y : 0) + n();
      cur = { nodes: [], closed: false };
      out.push(cur);
      node(x, y);
      sx = x;
      sy = y;
      // further pairs are line-tos
      cmd = rel ? 'l' : 'L';
      lastC = lastQ = null;
      continue;
    }
    if (!cur) {
      cur = { nodes: [], closed: false };
      out.push(cur);
      node(x, y);
    }
    switch (C) {
      case 'L':
        x = (rel ? x : 0) + n();
        y = (rel ? y : 0) + n();
        node(x, y);
        lastC = lastQ = null;
        break;
      case 'H':
        x = (rel ? x : 0) + n();
        node(x, y);
        lastC = lastQ = null;
        break;
      case 'V':
        y = (rel ? y : 0) + n();
        node(x, y);
        lastC = lastQ = null;
        break;
      case 'C': {
        const b = rel ? [x, y] : [0, 0];
        const x1 = b[0] + n(), y1 = b[1] + n(), x2 = b[0] + n(), y2 = b[1] + n();
        x = b[0] + n();
        y = b[1] + n();
        cubic(x1, y1, x2, y2, x, y);
        lastC = [x2, y2];
        lastQ = null;
        break;
      }
      case 'S': {
        const b = rel ? [x, y] : [0, 0];
        const x1 = lastC ? 2 * x - lastC[0] : x;
        const y1 = lastC ? 2 * y - lastC[1] : y;
        const x2 = b[0] + n(), y2 = b[1] + n();
        x = b[0] + n();
        y = b[1] + n();
        cubic(x1, y1, x2, y2, x, y);
        lastC = [x2, y2];
        lastQ = null;
        break;
      }
      case 'Q':
      case 'T': {
        const b = rel ? [x, y] : [0, 0];
        let qx: number, qy: number;
        if (C === 'Q') {
          qx = b[0] + n();
          qy = b[1] + n();
        } else {
          qx = lastQ ? 2 * x - lastQ[0] : x;
          qy = lastQ ? 2 * y - lastQ[1] : y;
        }
        const px = b[0] + n(), py = b[1] + n();
        cubic(x + (2 / 3) * (qx - x), y + (2 / 3) * (qy - y), px + (2 / 3) * (qx - px), py + (2 / 3) * (qy - py), px, py);
        x = px;
        y = py;
        lastQ = [qx, qy];
        lastC = null;
        break;
      }
      case 'A': {
        const rx = n(), ry = n(), rot = n(), large = n(), sweep = n();
        const px = (rel ? x : 0) + n();
        const py = (rel ? y : 0) + n();
        for (const c of arcToCubics(x, y, rx, ry, rot, !!large, !!sweep, px, py)) cubic(c[0], c[1], c[2], c[3], c[4], c[5]);
        x = px;
        y = py;
        lastC = lastQ = null;
        break;
      }
      default:
        // unknown command: skip its numbers
        while (i < toks.length && !isCmd(toks[i])) i++;
    }
  }
  return out.filter((c) => c.nodes.length >= 12 || (c.nodes.length >= 6 && c.closed));
}

/** An elliptical arc as cubic curves (SVG implementation notes, F.6). */
function arcToCubics(x1: number, y1: number, rx: number, ry: number, angle: number, large: boolean, sweep: boolean, x2: number, y2: number): number[][] {
  if (rx === 0 || ry === 0 || (x1 === x2 && y1 === y2)) return [[x1, y1, x2, y2, x2, y2]];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const phi = (angle * Math.PI) / 180;
  const cp = Math.cos(phi);
  const sp = Math.sin(phi);
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cp * dx + sp * dy;
  const y1p = -sp * dx + cp * dy;
  const lam = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lam > 1) {
    rx *= Math.sqrt(lam);
    ry *= Math.sqrt(lam);
  }
  const num2 = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  let co = Math.sqrt(Math.max(0, num2 / den));
  if (large === sweep) co = -co;
  const cxp = (co * rx * y1p) / ry;
  const cyp = (-co * ry * x1p) / rx;
  const cx = cp * cxp - sp * cyp + (x1 + x2) / 2;
  const cy = sp * cxp + cp * cyp + (y1 + y2) / 2;
  const ang = (ux: number, uy: number, vx: number, vy: number) => {
    const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return a;
  };
  const t1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dt = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dt > 0) dt -= 2 * Math.PI;
  if (sweep && dt < 0) dt += 2 * Math.PI;
  const segs = Math.max(1, Math.ceil(Math.abs(dt) / (Math.PI / 2)));
  const step = dt / segs;
  const k = (4 / 3) * Math.tan(step / 4);
  const pt = (t: number): [number, number] => [cx + rx * Math.cos(t) * cp - ry * Math.sin(t) * sp, cy + rx * Math.cos(t) * sp + ry * Math.sin(t) * cp];
  const der = (t: number): [number, number] => [-rx * Math.sin(t) * cp - ry * Math.cos(t) * sp, -rx * Math.sin(t) * sp + ry * Math.cos(t) * cp];
  const out: number[][] = [];
  for (let s = 0; s < segs; s++) {
    const a = t1 + s * step;
    const b = a + step;
    const [ax, ay] = pt(a);
    const [bx, by] = pt(b);
    const [dax, day] = der(a);
    const [dbx, dby] = der(b);
    out.push([ax + k * dax, ay + k * day, bx - k * dbx, by - k * dby, bx, by]);
  }
  out[out.length - 1][4] = x2;
  out[out.length - 1][5] = y2;
  return out;
}

const ellipseContour = (cx: number, cy: number, rx: number, ry: number): Contour => {
  const k = 0.5522847498;
  return {
    closed: true,
    nodes: [cx + rx, cy, cx + rx, cy - ry * k, cx + rx, cy + ry * k, cx, cy + ry, cx + rx * k, cy + ry, cx - rx * k, cy + ry, cx - rx, cy, cx - rx, cy + ry * k, cx - rx, cy - ry * k, cx, cy - ry, cx - rx * k, cy - ry, cx + rx * k, cy - ry],
  };
};
const polyContour = (pts: number[], closed: boolean): Contour => {
  const nodes: number[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) nodes.push(pts[i], pts[i + 1], pts[i], pts[i + 1], pts[i], pts[i + 1]);
  return { nodes, closed };
};

// ---------- styles ----------

interface Paint {
  fill: string;
  stroke: string;
  strokeWidth: number;
  fillOpacity: number;
  strokeOpacity: number;
  dash: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  textAnchor: string;
  color: string;
  markerStart: string;
  markerEnd: string;
  visibility: string;
}
const ROOT: Paint = { fill: 'black', stroke: 'none', strokeWidth: 1, fillOpacity: 1, strokeOpacity: 1, dash: 'none', fontSize: 16, fontFamily: '', fontWeight: 'normal', fontStyle: 'normal', textAnchor: 'start', color: 'black', markerStart: 'none', markerEnd: 'none', visibility: 'visible' };
const PROPS: Record<string, keyof Paint> = {
  fill: 'fill',
  stroke: 'stroke',
  'stroke-width': 'strokeWidth',
  'fill-opacity': 'fillOpacity',
  'stroke-opacity': 'strokeOpacity',
  'stroke-dasharray': 'dash',
  'font-size': 'fontSize',
  'font-family': 'fontFamily',
  'font-weight': 'fontWeight',
  'font-style': 'fontStyle',
  'text-anchor': 'textAnchor',
  color: 'color',
  'marker-start': 'markerStart',
  'marker-end': 'markerEnd',
  visibility: 'visibility',
};

function setProp(p: Paint, name: string, value: string): void {
  const key = PROPS[name];
  if (!key || value === 'inherit') return;
  const v = value.replace(/\s*!important\s*$/, '').trim();
  if (key === 'strokeWidth' || key === 'fillOpacity' || key === 'strokeOpacity' || key === 'fontSize') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) (p[key] as number) = v.endsWith('%') && key !== 'fontSize' ? n / 100 : n;
  } else (p[key] as string) = v;
}

function declarations(css: string): [string, string][] {
  return css
    .split(';')
    .map((d) => d.split(':'))
    .filter((d) => d.length >= 2)
    .map((d) => [d[0].trim().toLowerCase(), d.slice(1).join(':').trim()]);
}

const colorCtx = (() => {
  try {
    return new OffscreenCanvas(1, 1).getContext('2d');
  } catch {
    return null;
  }
})();

/** Any CSS color as #rrggbb or #rrggbbaa (alpha multiplied by `opacity`); null for none. */
function cssToHex(value: string, opacity: number, doc: Document, current: string): string | null {
  let v = value.trim();
  if (!v || v === 'none' || v === 'transparent') return null;
  if (v === 'currentColor' || v === 'currentcolor') v = current;
  const url = /^url\(\s*['"]?#([^'")]+)['"]?\s*\)/.exec(v);
  if (url) {
    // a gradient or pattern: its first color
    const ref = doc.getElementById(url[1]);
    const stop = ref?.querySelector('stop');
    if (!stop) return null;
    const st = Object.fromEntries(declarations(stop.getAttribute('style') ?? ''));
    v = st['stop-color'] ?? stop.getAttribute('stop-color') ?? 'black';
    opacity *= num(st['stop-opacity'] ?? stop.getAttribute('stop-opacity'), 1);
  }
  if (!colorCtx) return /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : null;
  colorCtx.fillStyle = '#000000';
  colorCtx.fillStyle = v;
  const c = String(colorCtx.fillStyle);
  let hex: string;
  let a = 1;
  if (c.startsWith('#')) hex = c.slice(0, 7);
  else {
    const m = /rgba?\(([^)]+)\)/.exec(c);
    if (!m) return null;
    const parts = m[1].split(',').map((s) => parseFloat(s));
    hex = '#' + parts.slice(0, 3).map((n) => Math.round(n).toString(16).padStart(2, '0')).join('');
    a = parts[3] ?? 1;
  }
  a *= opacity;
  if (a <= 0.002) return null;
  return a >= 0.998 ? hex : hex + Math.round(a * 255).toString(16).padStart(2, '0');
}

// ---------- the walk ----------

export function parseSvg(text: string): SvgImport {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) throw new Error('not an SVG');
  // <style> rules, applied by matching their selectors
  const rules: { sel: string; decls: [string, string][] }[] = [];
  for (const st of Array.from(doc.querySelectorAll('style'))) {
    const css = (st.textContent ?? '').replace(/\/\*[\s\S]*?\*\//g, '');
    for (const block of css.split('}')) {
      const [sel, body] = block.split('{');
      if (!sel || !body || sel.trim().startsWith('@')) continue;
      rules.push({ sel: sel.trim(), decls: declarations(body) });
    }
  }
  const vb = (root.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number);
  const hasVb = vb.length === 4 && vb.every(Number.isFinite) && vb[2] > 0 && vb[3] > 0;
  const width = num(root.getAttribute('width'), hasVb ? vb[2] : 300);
  const height = num(root.getAttribute('height'), hasVb ? vb[3] : 150);
  const rootM: M = hasVb ? [width / vb[2], 0, 0, height / vb[3], (-vb[0] * width) / vb[2], (-vb[1] * height) / vb[3]] : I;

  const shapes: SvgShape[] = [];
  let skipped = 0;
  let seen = 0;

  const paintOf = (el: Element, parent: Paint): Paint => {
    const p = { ...parent };
    for (const name of Object.keys(PROPS)) {
      const v = el.getAttribute(name);
      if (v !== null) setProp(p, name, v);
    }
    for (const r of rules) {
      let hit = false;
      try {
        hit = el.matches(r.sel);
      } catch {
        hit = false;
      }
      if (hit) for (const [k, v] of r.decls) setProp(p, k, v);
    }
    for (const [k, v] of declarations(el.getAttribute('style') ?? '')) setProp(p, k, v);
    return p;
  };
  const opacityOf = (el: Element): number => {
    const own = Object.fromEntries(declarations(el.getAttribute('style') ?? ''));
    return Math.min(1, Math.max(0, num(own['opacity'] ?? el.getAttribute('opacity'), 1)));
  };

  const styleFor = (p: Paint, m: M, opacity: number, closedFill: boolean): Partial<Style> => {
    const k = scaleOf(m);
    const fillHex = closedFill ? cssToHex(p.fill, p.fillOpacity, doc, p.color) : null;
    const strokeHex = cssToHex(p.stroke, p.strokeOpacity, doc, p.color);
    const fill: Fill = fillHex ? { kind: 'solid', color: fillHex } : { kind: 'none' };
    const dashed = p.dash && p.dash !== 'none' ? p.dash.split(/[\s,]+/).map(Number).filter((n) => n > 0) : [];
    const sw = Math.max(0, p.strokeWidth * k);
    const stroke: Stroke | null = strokeHex && sw > 0 ? { color: strokeHex, width: round(sw), dash: dashed.length ? (dashed[0] <= p.strokeWidth * 1.5 ? 'dotted' : 'dashed') : 'solid' } : null;
    return { fill, stroke, opacity: round(opacity) };
  };

  const pathShape = (contours: Contour[], p: Paint, m: M, opacity: number): void => {
    const world = contours.map((c) => {
      const nodes = c.nodes.slice();
      for (let i = 0; i < nodes.length; i += 2) [nodes[i], nodes[i + 1]] = apply(m, nodes[i], nodes[i + 1]);
      return { nodes, closed: c.closed };
    });
    const filled = cssToHex(p.fill, p.fillOpacity, doc, p.color) !== null;
    const flat = world.map((c) => ({ ...c, pts: flattenBezier(c.nodes, c.closed, 0.25) }));
    // one filled outline with holes inside it → one path with holes; otherwise a path per contour
    const boxes = flat.map((c) => bbox(c.pts));
    const outer = boxes.reduce((bi, b, i) => (b.w * b.h > boxes[bi].w * boxes[bi].h ? i : bi), 0);
    const nested = filled && flat.length > 1 && flat.every((c) => c.closed) && boxes.every((b, i) => i === outer || inside(b, boxes[outer]));
    const groups = nested ? [[outer, ...flat.map((_, i) => i).filter((i) => i !== outer)]] : flat.map((_, i) => [i]);
    for (const g of groups) {
      const main = flat[g[0]];
      const holes = g.slice(1).map((i) => flat[i].pts);
      const b = bbox([main.pts, ...holes].flat());
      if (!(b.w > 0 || b.h > 0)) continue;
      const w = Math.max(b.w, 0.01);
      const h = Math.max(b.h, 0.01);
      const local = (a: number[]) => a.map((v, i) => round(v - (i % 2 ? b.y : b.x)));
      const props: Record<string, unknown> = { nodes: local(main.nodes), points: local(main.pts), closed: main.closed };
      if (holes.length) props['holes'] = holes.map(local);
      shapes.push({ type: CoreTypes.path, x: b.x, y: b.y, w, h, rotation: 0, props, style: styleFor(p, m, opacity, main.closed) });
    }
  };

  /** A rectangle or ellipse stays one when the transform only moves, scales evenly and turns it. */
  const boxShape = (type: string, x: number, y: number, w: number, h: number, p: Paint, m: M, opacity: number, radius = 0): boolean => {
    const sx = Math.hypot(m[0], m[1]);
    const sy = Math.hypot(m[2], m[3]);
    const orthogonal = Math.abs(m[0] * m[2] + m[1] * m[3]) < 1e-6 && m[0] * m[3] - m[1] * m[2] > 0;
    if (!orthogonal) return false;
    const rot = Math.atan2(m[1], m[0]);
    const [cx, cy] = apply(m, x + w / 2, y + h / 2);
    const W = w * sx;
    const H = h * sy;
    const style = styleFor(p, m, opacity, true);
    if (radius > 0) style.cornerRadius = round(radius * Math.min(sx, sy));
    else if (type === CoreTypes.rect) style.cornerRadius = 0;
    shapes.push({ type, x: cx - W / 2, y: cy - H / 2, w: W, h: H, rotation: Math.abs(rot) < 1e-9 ? 0 : rot, props: { text: '' }, style });
    return true;
  };

  const walk = (el: Element, parent: Paint, pm: M, opacity: number): void => {
    const tag = el.localName;
    if (['defs', 'clipPath', 'mask', 'symbol', 'marker', 'pattern', 'linearGradient', 'radialGradient', 'filter', 'style', 'title', 'desc', 'metadata', 'script', 'foreignObject'].includes(tag)) return;
    const display = el.getAttribute('display') ?? Object.fromEntries(declarations(el.getAttribute('style') ?? ''))['display'];
    if (display === 'none') return;
    if (++seen > MAX_SVG_ELEMENTS) {
      skipped++;
      return;
    }
    const p = paintOf(el, parent);
    const op = opacity * opacityOf(el);
    const m = mul(pm, parseTransform(el.getAttribute('transform')));
    const hidden = p.visibility === 'hidden' || p.visibility === 'collapse';
    const a = (n: string, d = 0) => num(el.getAttribute(n), d);
    switch (tag) {
      case 'svg':
      case 'g':
      case 'a':
      case 'switch': {
        const inner = tag === 'svg' && el !== root ? mul(m, [1, 0, 0, 1, a('x'), a('y')]) : m;
        for (const c of Array.from(el.children)) walk(c, p, inner, op);
        return;
      }
      case 'use': {
        const id = (el.getAttribute('href') ?? el.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ?? '').replace(/^#/, '');
        const ref = id ? doc.getElementById(id) : null;
        if (!ref || ref.contains(el)) return;
        const mm = mul(m, [1, 0, 0, 1, a('x'), a('y')]);
        if (ref.localName === 'symbol') for (const c of Array.from(ref.children)) walk(c, p, mm, op);
        else walk(ref, p, mm, op);
        return;
      }
    }
    if (hidden) return;
    switch (tag) {
      case 'rect': {
        const w = a('width');
        const h = a('height');
        if (w <= 0 || h <= 0) return;
        const r = Math.min(a('rx', a('ry')), w / 2, h / 2);
        if (!boxShape(CoreTypes.rect, a('x'), a('y'), w, h, p, m, op, r)) pathShape([polyContour([a('x'), a('y'), a('x') + w, a('y'), a('x') + w, a('y') + h, a('x'), a('y') + h], true)], p, m, op);
        return;
      }
      case 'circle':
      case 'ellipse': {
        const rx = tag === 'circle' ? a('r') : a('rx', a('ry'));
        const ry = tag === 'circle' ? a('r') : a('ry', a('rx'));
        if (rx <= 0 || ry <= 0) return;
        const cx = a('cx');
        const cy = a('cy');
        if (!boxShape(CoreTypes.ellipse, cx - rx, cy - ry, rx * 2, ry * 2, p, m, op)) pathShape([ellipseContour(cx, cy, rx, ry)], p, m, op);
        return;
      }
      case 'line':
      case 'polyline': {
        const raw = tag === 'line' ? [a('x1'), a('y1'), a('x2'), a('y2')] : (el.getAttribute('points') ?? '').split(/[\s,]+/).filter(Boolean).map(Number);
        const pts: number[] = [];
        for (let i = 0; i + 1 < raw.length; i += 2) pts.push(...apply(m, raw[i], raw[i + 1]));
        if (pts.length < 4 || pts.some((v) => !Number.isFinite(v))) return;
        const filledPoly = tag === 'polyline' && cssToHex(p.fill, p.fillOpacity, doc, p.color) !== null;
        if (filledPoly) return pathShape([polyContour(raw, false)], p, m, op);
        const b = bbox(pts);
        const style = styleFor(p, m, op, false);
        if (!style.stroke) return;
        shapes.push({
          type: CoreTypes.line,
          x: b.x,
          y: b.y,
          w: Math.max(b.w, 0.01),
          h: Math.max(b.h, 0.01),
          rotation: 0,
          props: { points: pts.map((v, i) => round(v - (i % 2 ? b.y : b.x))), arrowStart: p.markerStart !== 'none', arrowEnd: p.markerEnd !== 'none' },
          style,
        });
        return;
      }
      case 'polygon': {
        const raw = (el.getAttribute('points') ?? '').split(/[\s,]+/).filter(Boolean).map(Number);
        if (raw.length >= 6 && raw.every(Number.isFinite)) pathShape([polyContour(raw, true)], p, m, op);
        return;
      }
      case 'path': {
        const contours = parsePathData(el.getAttribute('d') ?? '');
        if (contours.length) pathShape(contours, p, m, op);
        return;
      }
      case 'text': {
        textShape(el, p, m, op);
        return;
      }
      case 'image': {
        const href = el.getAttribute('href') ?? el.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ?? '';
        const blob = dataUrlBlob(href);
        const w = a('width');
        const h = a('height');
        if (!blob || w <= 0 || h <= 0) {
          skipped++;
          return;
        }
        const sx = Math.hypot(m[0], m[1]);
        const sy = Math.hypot(m[2], m[3]);
        const [cx, cy] = apply(m, a('x') + w / 2, a('y') + h / 2);
        shapes.push({ type: CoreTypes.image, x: cx - (w * sx) / 2, y: cy - (h * sy) / 2, w: w * sx, h: h * sy, rotation: Math.atan2(m[1], m[0]), props: {}, style: { opacity: round(op) }, image: blob });
        return;
      }
      default:
        skipped++;
    }
  };

  const textShape = (el: Element, p: Paint, m: M, opacity: number): void => {
    // the text with its pieces (tspan) on one line; a tspan with its own y starts a new line
    const lines: string[] = [];
    let line = '';
    let x = num(el.getAttribute('x'));
    let y = num(el.getAttribute('y'));
    let first = true;
    const visit = (n: Node) => {
      if (n.nodeType === 3) line += n.nodeValue ?? '';
      else if (n.nodeType === 1 && (n as Element).localName === 'tspan') {
        const t = n as Element;
        if (t.hasAttribute('y') && line.trim()) {
          lines.push(line);
          line = '';
        }
        if (first && t.hasAttribute('x')) x = num(t.getAttribute('x'));
        if (first && t.hasAttribute('y')) y = num(t.getAttribute('y'));
        first = false;
        for (const c of Array.from(t.childNodes)) visit(c);
      }
    };
    for (const c of Array.from(el.childNodes)) visit(c);
    lines.push(line);
    const content = lines.map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean).join('\n');
    if (!content) return;
    const k = scaleOf(m);
    const size = Math.max(1, p.fontSize * k);
    const color = cssToHex(p.fill, p.fillOpacity, doc, p.color) ?? '#000000';
    const family = p.fontFamily.replace(/["']/g, '').split(',').map((s) => s.trim()).find((n) => FONTS.some((f) => f.name && f.name.toLowerCase() === n.toLowerCase()));
    const font = family ? FONTS.find((f) => f.name.toLowerCase() === family.toLowerCase()) : undefined;
    const bold = /bold|[6-9]00/.test(p.fontWeight);
    const italic = /italic|oblique/.test(p.fontStyle);
    const align: TextStyle['align'] = p.textAnchor === 'middle' ? 'center' : p.textAnchor === 'end' ? 'right' : 'left';
    const textStyle: TextStyle = { ...DEFAULT_TEXT_STYLE, fontSize: round(size), color, align, valign: 'top', ...(font ? { fontFamily: font.family } : {}), ...(bold ? { bold: true } : {}), ...(italic ? { italic: true } : {}) };
    const longest = Math.max(...content.split('\n').map((l) => measure(l, size, bold, italic, font?.family)));
    const pad = size * 0.45;
    const w = longest + pad * 2 + 2;
    const lineH = size * (textStyle.lineHeight ?? 1.25);
    const h = lineH * content.split('\n').length + pad * 2;
    const [ax, ay] = apply(m, x, y);
    const left = align === 'center' ? ax - w / 2 : align === 'right' ? ax - w + pad : ax - pad;
    // the baseline of the first line sits about 0.8 em below the top of its line box
    const top = ay - pad - (lineH - size) / 2 - size * 0.8;
    shapes.push({ type: CoreTypes.text, x: left, y: top, w, h, rotation: Math.atan2(m[1], m[0]), props: { text: content }, style: { text: textStyle, opacity: round(opacity), fill: { kind: 'none' }, stroke: null } });
  };

  walk(root, ROOT, rootM, 1);
  return { shapes, width, height, skipped };
}

function measure(text: string, size: number, bold: boolean, italic: boolean, family?: string): number {
  if (!colorCtx) return text.length * size * 0.55;
  colorCtx.font = `${italic ? 'italic ' : ''}${bold ? '700 ' : ''}${size}px ${family ?? DEFAULT_TEXT_STYLE.fontFamily ?? 'sans-serif'}`;
  return colorCtx.measureText(text).width;
}

function dataUrlBlob(href: string): Blob | null {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(href.trim());
  if (!m || !m[1].startsWith('image/')) return null;
  try {
    const bin = m[2] ? atob(m[3].replace(/\s+/g, '')) : decodeURIComponent(m[3]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: m[1] });
  } catch {
    return null;
  }
}

const round = (v: number) => Math.round(v * 1000) / 1000;

function bbox(pts: number[]): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < pts.length; i += 2) {
    x0 = Math.min(x0, pts[i]);
    x1 = Math.max(x1, pts[i]);
    y0 = Math.min(y0, pts[i + 1]);
    y1 = Math.max(y1, pts[i + 1]);
  }
  return Number.isFinite(x0) ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : { x: 0, y: 0, w: 0, h: 0 };
}

const inside = (a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) => a.x >= b.x - 1e-6 && a.y >= b.y - 1e-6 && a.x + a.w <= b.x + b.w + 1e-6 && a.y + a.h <= b.y + b.h + 1e-6;

// ---------- onto the canvas ----------

/**
 * Put an SVG drawing onto the canvas as objects (one undoable step, grouped when there are several),
 * centered where the pointer is (or the view center) at the size it looks at the current zoom.
 * Returns how many objects were made, or null when the text is not a usable SVG.
 */
export async function importSvgText(text: string, putImage: (b: Blob) => Promise<string | null>): Promise<{ count: number; skipped: number } | null> {
  const ed = live.editor;
  if (!ed || ed.readOnly) return null;
  let parsed: SvgImport;
  try {
    parsed = parseSvg(text);
  } catch {
    return null;
  }
  const shapes = parsed.shapes;
  if (!shapes.length) return { count: 0, skipped: parsed.skipped };
  for (const s of shapes) if (s.image) s.props['attachment'] = await putImage(s.image);
  const usable = shapes.filter((s) => !s.image || s.props['attachment']);
  const b = bbox(usable.flatMap((s) => [s.x, s.y, s.x + s.w, s.y + s.h]));
  const k = ed.viewScale;
  const c = ed.pointerWorldOrCenter();
  const X = (v: number) => c.x + (v - (b.x + b.w / 2)) * k;
  const Y = (v: number) => c.y + (v - (b.y + b.h / 2)) * k;
  const scaled = (a: unknown) => (Array.isArray(a) ? a.map((v) => (typeof v === 'number' ? round(v * k) : Array.isArray(v) ? v.map((u: number) => round(u * k)) : v)) : a);
  const ids = ed.edit('import-svg', (tx) => {
    let z = commands.nextZ(ed.doc);
    const made: string[] = [];
    for (const s of usable) {
      const props = { ...s.props };
      for (const key of ['points', 'nodes', 'holes']) if (key in props) props[key] = scaled(props[key]);
      const style: Partial<Style> = { ...s.style };
      if (style.stroke) style.stroke = { ...style.stroke, width: round(style.stroke.width * k) };
      if (style.text) style.text = { ...style.text, fontSize: round(style.text.fontSize * k) };
      if (style.cornerRadius) style.cornerRadius = round(style.cornerRadius * k);
      const o = makeObject(ed.doc.canvasId, { type: s.type, x: X(s.x), y: Y(s.y), w: s.w * k, h: s.h * k, rotation: s.rotation, z, props, style });
      tx.create(o);
      made.push(o.id);
      z = keyBetween(z, null);
    }
    const g = made.length > 1 ? commands.group(tx, made) : null;
    commands.assignFrames(tx, g ? [g] : made);
    return g ? [g] : made;
  });
  if (ids) ed.select(ids);
  return { count: usable.length, skipped: parsed.skipped + shapes.length - usable.length };
}
